import { defineFeature } from '../define-feature';
import { TEST_IDS, sidebarNavTestId, sidebarSubTestId } from '../test-ids';

export default defineFeature({
  id: 'marketing.campaign.email.schedule',
  category: 'marketing',
  title: "Programmer l'envoi d'une campagne email",
  intent:
    "Programmer l'envoi d'une campagne email existante à une date et une heure futures précises — l'envoi part alors automatiquement à l'échéance, sans risque d'envoi immédiat incontrôlé ou d'oubli.",
  context: {
    why: "Programmer l'envoi d'une campagne plutôt que l'envoyer manuellement présente plusieurs avantages majeurs. D'abord, vous pouvez préparer vos campagnes en avance, quand vous avez du temps et de la disponibilité mentale, et laisser l'envoi partir tout seul au moment stratégique — que ce soit le lundi matin pour une offre de la semaine, le jeudi soir pour un week-end de promotion, ou le 1er décembre pour lancer votre campagne de Noël. Ensuite, vous protégez votre communication contre les envois précipités : la programmation vous donne un tampon entre le moment où vous cliquez sur « envoyer » et le moment où le message part réellement, ce qui vous laisse la possibilité de revenir en arrière si vous détectez une erreur. Enfin, vous pouvez orchestrer plusieurs campagnes en séquence sans avoir à les surveiller — vous les programmez toutes une fois, elles s'exécutent d'elles-mêmes selon le calendrier. Un point important à retenir avant de valider : programmez la date et l'heure avec soin, parce que l'envoi part vraiment tout seul à l'échéance. Si vous avez un doute sur le rendu final de votre email, faites un envoi de test sur votre propre boîte pour vérifier avant de programmer. C'est particulièrement recommandé quand vous avez utilisé Pulse pour la rédaction — même si l'IA fait un bon travail, un dernier coup d'œil humain reste précieux.",
    use_cases: [
      "Vous préparez une campagne d'offre spéciale pour la semaine prochaine : programmez l'envoi le lundi à 9h pour toucher vos clients en début de semaine.",
      'Vous voulez lancer votre campagne de Noël le 1er décembre à minuit : programmez-la dès novembre pour être tranquille.',
      'Vous préparez plusieurs campagnes saisonnières (janvier, février, mars) : programmez-les toutes en une seule session de travail.',
      'Vous voulez éviter les envois précipités le soir tardif ou le week-end : programmez systématiquement pour des créneaux professionnels.',
      "Vous testez un envoi et vous voulez le lancer sur vous-même avant la vraie campagne : utilisez l'envoi de test pour vérifier le rendu.",
    ],
  },
  required_scopes: ['campaign.email.update'],
  prerequisites: [
    {
      ref: 'marketing.campaign.email.create',
      why: 'La campagne (objet + destinataires) doit déjà exister en brouillon.',
    },
  ],
  ui_entry_points: [{ route: '/marketing/campaigns', element: 'Bouton « Programmer » sur une campagne en brouillon' }],
  api: {
    primary_operation_id: 'ProCampaignAppController_scheduleCampaign',
    related_operation_ids: ['ProCampaignAppController_sendTestCampaign', 'ProCampaignAppController_getProCampaign'],
  },
  steps: [
    'Choisir la campagne brouillon à programmer.',
    "Définir la date et l'heure d'envoi (futures).",
    "Valider — la campagne passe au statut « programmée » et partira automatiquement à l'échéance.",
  ],
  gotchas: [
    "Seule une campagne au statut *brouillon* ou *déjà programmée* peut être (re)programmée : une campagne déjà envoyée ne l'est plus.",
    "Avant de programmer, proposer au pro un *envoi de test* (`sendTestCampaign`) pour qu'il vérifie le rendu sur sa propre boîte — recommandé puisque Pulse ne maîtrise pas le contenu visuel.",
    "`scheduled_at` est une date-heure future. Confirmer la date avec le pro avant de programmer (l'envoi part tout seul à l'échéance).",
    'Pour revenir en arrière, déprogrammer la campagne la repasse en brouillon (`unscheduleCampaign`).',
  ],
  related_features: ['marketing.campaign.email.create'],
  demo_script: {
    // La programmation est une action de ligne (menu « … ») sur une campagne au statut
    // « brouillon », dans un menu déroulant sans cible stable → démo sobre qui montre la
    // liste des campagnes d'où l'on programme. À enrichir quand l'action de ligne sera
    // instrumentée (test-id) et qu'un brouillon sera seedé.
    duration_target_s: 20,
    steps: [
      {
        action: 'navigate',
        value: '/fr-FR',
        caption: 'Programmer l’envoi vous laisse préparer vos campagnes à l’avance et les faire partir au bon moment.',
      },
      {
        action: 'click',
        target: sidebarNavTestId('/marketing'),
        caption: 'Dans le menu latéral, ouvrez « Communication ».',
      },
      { action: 'click', target: sidebarSubTestId('/marketing/campaigns'), caption: 'Puis « Campagne marketing ».' },
      {
        action: 'expect_visible',
        target: TEST_IDS.campaigns_create,
        caption:
          'Depuis votre liste de campagnes, l’envoi se programme via le menu d’une campagne en brouillon, et part seul à l’échéance.',
        capture: true,
      },
    ],
  },
});
